import { useState, useEffect, useCallback } from 'react';
import { biometricsApi, GENDER_OPTIONS, DATE_PRESET_OPTIONS } from '../api/biometricsApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';

// ── Dynamic Date Preset Helpers (same pattern as ManualPunch) ──

function getTodayISO() {
  return new Date().toISOString().split('T')[0];
}

function getYesterdayISO() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

function getThisMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(lastDay).padStart(2, '0')}` };
}

function getLastMonthRange() {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const y = lastMonth.getFullYear();
  const m = String(lastMonth.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(y, lastMonth.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(lastDay).padStart(2, '0')}` };
}

function getDefaultDateRange() {
  return getLastMonthRange();
}

export function useBiometricsList() {
  const defaultRange = getDefaultDateRange();

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // ── Legacy filters (matching BiometricRecordsDAL.GetList params) ──
  const [empId, setEmpId] = useState(null);           // AcHeadID
  const [companyId, setCompanyId] = useState(null);   // AC.CompanyID
  const [branchId, setBranchId] = useState(null);     // AC.Branch
  const [deptId, setDeptId] = useState(null);         // AC.DeptID
  const [gender, setGender] = useState('S');          // 'S' = All (legacy default)
  const [empGroupId, setEmpGroupId] = useState(null); // Ad.CustGroupID

  // ── Date filters ──
  const [datePresetFilter, setDatePresetFilter] = useState('Last Month');
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);

  // ── Search ──
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');

  // ── Pagination (server-side) ──
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await biometricsApi.getRecords(
        {
          empId: empId || undefined,
          companyId: companyId || undefined,
          branchId: branchId || undefined,
          deptId: deptId || undefined,
          gender: gender !== 'S' ? gender : undefined,
          empGroupId: empGroupId || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          search: searchText || undefined,
        },
        page,
        pageSize
      );
      setRecords(result.items || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error(err);
      appToast.error('Failed to fetch biometric records.');
    } finally {
      setIsLoading(false);
    }
  }, [empId, companyId, branchId, deptId, gender, empGroupId, fromDate, toDate, searchText, page, pageSize]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // ── Date preset handler (dynamic dates, not hardcoded) ──
  const handleDatePresetChange = (preset) => {
    setDatePresetFilter(preset);
    setPage(1);
    if (preset === 'Today') {
      const today = getTodayISO();
      setFromDate(today);
      setToDate(today);
    } else if (preset === 'Yesterday') {
      const yesterday = getYesterdayISO();
      setFromDate(yesterday);
      setToDate(yesterday);
    } else if (preset === 'This Month') {
      const range = getThisMonthRange();
      setFromDate(range.from);
      setToDate(range.to);
    } else if (preset === 'Last Month') {
      const range = getLastMonthRange();
      setFromDate(range.from);
      setToDate(range.to);
    }
    // Custom: user sets fromDate/toDate manually
  };

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const resetFilters = () => {
    const range = getLastMonthRange();
    setEmpId(null);
    setCompanyId(null);
    setBranchId(null);
    setDeptId(null);
    setGender('S');
    setEmpGroupId(null);
    setDatePresetFilter('Last Month');
    setFromDate(range.from);
    setToDate(range.to);
    setSearchField('Name');
    setSearchText('');
    setPage(1);
  };

  // ── SharedFilters compatibility shims ──
  // SharedFilters uses string-based "All" convention; we map to legacy IDs here
  const setCompanyFilter = (val) => {
    setCompanyId(val && val !== 'All' ? (isNaN(Number(val)) ? null : Number(val)) : null);
    setPage(1);
  };
  const setBranchFilter = (val) => {
    setBranchId(val && val !== 'All' ? (isNaN(Number(val)) ? null : Number(val)) : null);
    setPage(1);
  };
  const setDepartmentFilter = (val) => {
    setDeptId(val && val !== 'All' ? (isNaN(Number(val)) ? null : Number(val)) : null);
    setPage(1);
  };
  const setGenderFilter = (val) => {
    setGender(val && val !== 'All' ? val : 'S');
    setPage(1);
  };
  const setEmpGroupFilter = (val) => {
    setEmpGroupId(val && val !== 'All' ? (isNaN(Number(val)) ? null : Number(val)) : null);
    setPage(1);
  };
  const setEmployeeFilter = (val) => {
    setEmpId(val && val !== 'All' ? (isNaN(Number(val)) ? null : Number(val)) : null);
    setPage(1);
  };

  // ── Tool actions ──
  const handleDelete = async (swipeId, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Swipe Record',
      message: `Are you sure you want to delete the swipe record for "${name}"?`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await biometricsApi.deleteSwipe(swipeId);
        appToast.success(`Swipe record deleted successfully.`);
        fetchRecords();
      } catch (err) {
        console.error(err);
        appToast.error('Failed to delete swipe record.');
      }
    }
  };

  const handleToolSelect = (toolKey) => {
    if (toolKey === 'upload') {
      setIsUploadModalOpen(true);
    } else if (toolKey === 'download_template') {
      biometricsApi.downloadTemplate().catch(() => appToast.error('Failed to download template.'));
    } else if (toolKey === 'print') {
      window.print();
    } else if (toolKey === 'export_csv') {
      if (records.length === 0) {
        appToast.error('No biometric records to export.');
        return;
      }
      // Legacy columns: IDCardNo, Name, Description, Date, First Swipe, Last Swipe, M, Swipes, Attendance
      const headers = ['IDCard No', 'Name', 'Company-Dept-Branch', 'Date', 'First Swipe', 'Last Swipe', 'Manual', 'Swipes', 'Attendance'];
      const rows = records.map((r) => [
        `"${r.idCardNo || ''}"`,
        `"${r.name || ''}"`,
        `"${r.description || ''}"`,
        `"${r.aDate || ''}"`,
        `"${r.firstSwipe || ''}"`,
        `"${r.lastSwipe || ''}"`,
        `"${r.manualPunch || ''}"`,
        r.totalSwipes || 0,
        `"${r.attendance || ''}"`,
      ]);
      const csvContent = 'data:text/csv;charset=utf-8,'
        + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
      const link = document.createElement('a');
      link.setAttribute('href', encodeURI(csvContent));
      link.setAttribute('download', `Biometric_Records_${getTodayISO()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      appToast.success('Exported biometric records to CSV.');
    } else {
      appToast.info(`Selected: ${toolKey}`);
    }
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  const searchFieldOptions = [
    { value: 'Name', label: 'Employee Name' },
    { value: 'IDCard', label: 'IDCard No' },
  ];

  const tools = [
    { key: 'download_template', label: 'Template', icon: 'download' },
    { key: 'upload', label: 'Upload', icon: 'cloud_upload' },
    { key: 'print', label: 'Print', icon: 'print' },
    { key: 'export_csv', label: 'Export CSV', icon: 'file_download' },
  ];

  return {
    state: {
      records,
      isLoading,
      isUploadModalOpen,

      // Filter state (string values for SharedFilters compatibility)
      companyFilter: companyId ? String(companyId) : 'All',
      branchFilter: branchId ? String(branchId) : 'All',
      departmentFilter: deptId ? String(deptId) : 'All',
      genderFilter: gender !== 'S' ? gender : 'All',
      empGroupFilter: empGroupId ? String(empGroupId) : 'All',
      employeeFilter: empId ? String(empId) : '',
      datePresetFilter,
      fromDate,
      toDate,
      searchField,
      searchText,

      // Pagination
      page,
      pageSize,
      totalCount: total,
      totalPages,

      // Options
      genderOptions: GENDER_OPTIONS,
      datePresetOptions: DATE_PRESET_OPTIONS,
      searchFieldOptions,
      tools,
    },
    actions: {
      setCompanyFilter,
      setBranchFilter,
      setDepartmentFilter,
      setGenderFilter,
      setEmpGroupFilter,
      setEmployeeFilter,
      setDatePresetFilter: handleDatePresetChange,
      setFromDate: (v) => { setFromDate(v); setPage(1); },
      setToDate: (v) => { setToDate(v); setPage(1); },
      setSearchField,
      setSearchText,
      setPage,
      setIsUploadModalOpen,
      handleSearch,
      resetFilters,
      handleDelete,
      handleToolSelect,
      refresh: fetchRecords,
    },
  };
}
